/**
 * Comprehensive list of medical specialties combining and extending from:
 * - SpecialtySettings.tsx (user-settings)
 * - Interstitials constants.ts
 *
 * Biased toward the SpecialtySettings.tsx format but enhanced with additional specialties.
 * Alphabetically ordered for consistency.
 */
export const MEDICAL_SPECIALTIES = [
  'Acupuncture',
  'Allergy and Immunology',
  'Anesthesiology',
  'Cardiology',
  'Child & Adolescent Psychiatry',
  'Chiropractic',
  'Clinical Nutrition',
  'Critical Care Medicine',
  'Dentistry',
  'Dermatology',
  'Dermatology and Venereology',
  'Emergency Medicine',
  'Endocrinology',
  'Family Medicine',
  'Gastroenterology',
  'General Practice',
  'General Surgery',
  'Geriatrics',
  'Hospitalist Medicine',
  'Infectious Disease',
  'Internal Medicine',
  'Nephrology',
  'Neurology',
  'Obstetrics and Gynecology',
  'Occupational Medicine',
  'Oncology',
  'Ophthalmology',
  'Orthopedics',
  'Orthopaedic Trauma',
  'Otolaryngology (ENT)',
  'Pain Management',
  'Pathology',
  'Pediatrics',
  'Physical Medicine & Rehabilitation',
  'Physical Therapy',
  'Plastic Surgery',
  'Podiatry',
  'Primary Care',
  'Psychiatry',
  'Psychology',
  'Pulmonology',
  'Radiology',
  'Rheumatology',
  'Sports Medicine',
  'Urology',
  'Venereology',
];

export type MedicalSpecialty = (typeof MEDICAL_SPECIALTIES)[number];

/**
 * Create specialty options in the format expected by form components
 */
export const createSpecialtyOptions = () =>
  MEDICAL_SPECIALTIES.map((specialty) => ({
    value: specialty.toUpperCase().replace(/[^A-Z0-9]/g, '_'),
    label: specialty,
  }));

/**
 * Helper to find specialty by partial match
 */
export const findSpecialtyByLabel = (label: string) =>
  MEDICAL_SPECIALTIES.find(
    (specialty) => specialty.toLowerCase() === label.toLowerCase(),
  );

/**
 * Type-safe helper to check if a string is a known medical specialty
 */
export const isKnownSpecialty = (
  specialty: string,
): specialty is MedicalSpecialty =>
  (MEDICAL_SPECIALTIES as readonly string[]).includes(specialty);

/**
 * Electronic Health Record (EHR) system options
 */
export const EHR_OPTIONS = [
  { value: 'EPIC', label: 'Epic' },
  { value: 'ORACLE_HEALTH', label: 'Oracle Health (Cerner)' },
  { value: 'GE_HEALTHCARE', label: 'GE HealthCare (Centricity)' },

  { value: 'ATHENA', label: 'athenahealth' },
  { value: 'KAREO', label: 'Kareo Clinical (Tebra)' },
  { value: 'ECLINICALWORKS', label: 'eClinicalWorks' },
  { value: 'PRACTICE_FUSION', label: 'Practice Fusion' },
  { value: 'DRCHRONO', label: 'DrChrono' },
  { value: 'ADVANCEDMD', label: 'AdvancedMD' },
  { value: 'SIMPLEPRACTICE', label: 'SimplePractice' },
  { value: 'ELATION', label: 'Elation Health' },
  { value: 'CARECLOUD', label: 'CareCloud' },
  { value: 'NEXTGEN', label: 'NextGen Healthcare' },
  { value: 'CANVAS', label: 'Canvas Medical' },
  { value: 'CUREMD', label: 'CureMD' },
  { value: 'PRAXIS_EMR', label: 'Praxis EMR' },
  { value: 'MODERNIZING_MEDICINE', label: 'ModMed (Modernizing Medicine)' },
  { value: 'GREENWAY', label: 'Greenway Health' },
  { value: 'AMAZING_CHARTS', label: 'Amazing Charts' },
  { value: 'VALANT', label: 'Valant' },
  { value: 'POINTCLICKCARE', label: 'PointClickCare' },
  { value: 'SIS', label: 'Surgical Information Systems (SIS)' },
  { value: 'WELLSKY', label: 'WellSky' },
  { value: 'NETSMART', label: 'Netsmart Technologies' },

  { value: 'OTHER', label: 'Other' },
  { value: 'NONE', label: 'No EHR System' },
] as const;

/**
 * Organization size options based on number of clinicians
 */
export const ORGANIZATION_SIZE_OPTIONS = [
  { value: 'SOLO', label: '1 clinician' },
  { value: 'SMALL', label: '2-10 clinicians' },
  { value: 'MEDIUM', label: '11-50 clinicians' },
  { value: 'LARGE', label: '51-200 clinicians' },
  { value: 'ENTERPRISE', label: '200+ clinicians' },
] as const;
